import {Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {EventBookingDTO, EventDTO, EventService} from '../api/event-api.service';
import {SpinnerService} from '../shared/services/spinner.service';
import {ToastrService} from 'ngx-toastr';
import {finalize, Subject} from 'rxjs';
import {UserApiService} from '../api/user-api.service';
import {CurrencyPipe} from '@angular/common';
import {DataTableDirective} from 'angular-datatables';
import {escapeHtml, ranaDtOptions, RanaDtOptions} from '../shared/rana-datatable-defaults';

@Component({
    selector: 'app-event-attendees',
    templateUrl: './event-attendees.component.html',
    styleUrls: ['./event-attendees.component.css'],
    providers: [CurrencyPipe]
})
export class EventAttendeesComponent implements OnInit, OnDestroy {
    // Two independent DataTables instances on one component — ViewChild-by-type only works
    // for a single instance, so these are looked up by their template reference variable
    // instead, explicitly reading the DataTableDirective (it has no `exportAs`, so a bare
    // template variable would otherwise resolve to the host ElementRef, not the directive).
    @ViewChild('bookingsDt', {read: DataTableDirective}) bookingsDtElement!: DataTableDirective;
    @ViewChild('regsDt', {read: DataTableDirective}) regsDtElement!: DataTableDirective;

    bookingsDtOptions: RanaDtOptions = ranaDtOptions({
        columns: [
            {title: 'Name', data: null, orderable: false, render: (d: any, t: any, row: any) => escapeHtml(this.userNames[row.userId] || row.userId)},
            {title: 'Options Selected', data: null, orderable: false, render: (d: any, t: any, row: any) => escapeHtml(this.formatOptions(row.selectedOptions || []))},
            {title: 'Total', data: 'totalAmount', render: (d: any) => this.currencyPipe.transform(d || 0, 'USD') || ''},
            {title: 'Status', data: 'status', render: (d: any) => `<span class="rana-badge rana-badge-neutral">${escapeHtml(d)}</span>`}
        ]
    });
    bookingsDtTrigger: Subject<any> = new Subject();

    regsDtOptions: RanaDtOptions = ranaDtOptions({
        columns: [
            {title: 'Name', data: null, orderable: false, render: (d: any, t: any, row: any) => escapeHtml(this.userNames[row.userId] || row.userId)},
            {title: 'Adults', data: 'adults'},
            {title: 'Children', data: 'children'},
            {title: 'Seniors', data: 'seniors'}
        ]
    });
    regsDtTrigger: Subject<any> = new Subject();

    @Input() externalEventId?: string;
    get embedded(): boolean { return !!this.externalEventId; }
    eventId: string = '';
    event: EventDTO | null = null;
    userNames: { [userId: string]: string } = {};
    bookings: EventBookingDTO[] = [];
    optionLabelMap: { [optionId: string]: string } = {};

    get isConference(): boolean { return this.event?.eventCategory === 'CONFERENCE'; }

    get totalAdults(): number {
        return (this.event?.eventRegistrations || []).reduce((s, r) => s + (Number(r.adults) || 0), 0);
    }
    get totalChildren(): number {
        return (this.event?.eventRegistrations || []).reduce((s, r) => s + (Number(r.children) || 0), 0);
    }
    get totalSeniors(): number {
        return (this.event?.eventRegistrations || []).reduce((s, r) => s + (Number(r.seniors) || 0), 0);
    }

    constructor(
        private route: ActivatedRoute,
        private eventService: EventService,
        private userApiService: UserApiService,
        private spinner: SpinnerService,
        private toastr: ToastrService,
        private currencyPipe: CurrencyPipe
    ) {}

    ngOnDestroy(): void {
        this.bookingsDtTrigger.unsubscribe();
        this.regsDtTrigger.unsubscribe();
    }

    // The outer *ngTemplateOutlet only instantiates these tables once `event` is truthy, so the
    // very first trigger must wait a tick for Angular's change detection to actually create the
    // <table> in the DOM — otherwise the DataTableDirective doesn't exist yet to subscribe and
    // this emission is lost, leaving the table permanently uninitialized.
    private refreshBookingsTable(): void {
        this.bookingsDtOptions.data = this.bookings;
        setTimeout(() => {
            if (this.bookingsDtElement?.dtInstance) {
                this.bookingsDtElement.dtInstance.then(dt => {
                    dt.destroy();
                    this.bookingsDtTrigger.next(null);
                });
            } else {
                this.bookingsDtTrigger.next(null);
            }
        });
    }

    private refreshRegistrationsTable(): void {
        this.regsDtOptions.data = this.event?.eventRegistrations || [];
        setTimeout(() => {
            if (this.regsDtElement?.dtInstance) {
                this.regsDtElement.dtInstance.then(dt => {
                    dt.destroy();
                    this.regsDtTrigger.next(null);
                });
            } else {
                this.regsDtTrigger.next(null);
            }
        });
    }

    ngOnInit(): void {
        if (this.externalEventId) {
            this.eventId = this.externalEventId;
            this.loadEvent();
        } else {
            this.route.paramMap.subscribe(params => {
                this.eventId = params.get('id') || '';
                if (this.eventId) this.loadEvent();
            });
        }
    }

    loadEvent(): void {
        this.spinner.show();
        this.eventService.getPublicEventFull(this.eventId).pipe(
            finalize(() => this.spinner.hide())
        ).subscribe({
            next: (event) => {
                this.event = event;
                (event.ticketOptions || []).forEach(opt => {
                    if (opt.optionId) this.optionLabelMap[opt.optionId] = opt.label;
                });
                if (event.eventCategory === 'CONFERENCE') {
                    this.loadBookings();
                } else {
                    this.resolveUserNames(event);
                    this.refreshRegistrationsTable();
                }
            },
            error: () => this.toastr.error('Failed to load event')
        });
    }

    loadBookings(): void {
        this.eventService.getPublicEventBookings(this.eventId).subscribe({
            next: (bookings) => {
                this.bookings = bookings;
                this.refreshBookingsTable();
                const uniqueIds = [...new Set(bookings.map(b => b.userId).filter(Boolean))] as string[];
                if (uniqueIds.length) {
                    this.userApiService.getUsersBatch(uniqueIds).subscribe({
                        next: (users) => {
                            users.forEach((data: any) => {
                                const fullName = `${data.firstname} ${data.lastname}`;
                                this.userNames[data.id] = data.spouseName
                                    ? `${fullName} & ${data.spouseName}` : fullName;
                            });
                            this.refreshBookingsTable();
                        },
                        error: () => {}
                    });
                }
            },
            error: () => this.toastr.error('Failed to load bookings')
        });
    }

    formatOptions(selectedOptions: any[]): string {
        if (!selectedOptions?.length) return '—';
        return selectedOptions
            .map(o => `${this.optionLabelMap[o.optionId] || o.optionId} ×${o.quantity}`)
            .join(', ');
    }

    private resolveUserNames(event: EventDTO): void {
        if (!event.eventRegistrations?.length) return;
        const uniqueIds = [...new Set(
            event.eventRegistrations.map((r: any) => r.userId).filter(Boolean)
        )] as string[];
        this.userApiService.getUsersBatch(uniqueIds).subscribe({
            next: (users) => {
                users.forEach((data: any) => {
                    const fullName = `${data.firstname} ${data.lastname}`;
                    this.userNames[data.id] = data.spouseName ? `${fullName} & ${data.spouseName}` : fullName;
                });
                this.refreshRegistrationsTable();
            },
            error: () => {}
        });
    }
}